import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  Paper,
  TextField,
  IconButton,
  useTheme,
  Badge,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import UserAccountDropdown from '../UserAccountDropdown/UserAccountDropdown';
import Footer from '../Layouts/Footer';
import logo from '../../assets/VisibeenLogo.png';

// Import custom icons
import SearchIcon from '../icons/SearchIcon';
import BarChartIcon from '../icons/BarChartIcon';
import TaskIcon from '../icons/TaskIcon';
import ResultsIcon from '../icons/ResultsIcon';
import TrophyIcon from '../icons/TrophyIcon';
import LightbulbIcon from '../icons/LightbulbIcon';
import PeopleIcon from '../icons/PeopleIcon';
import GrowthToolsIcon from '../icons/GrowthToolsIcon';
import ShopIcon from '../icons/ShopIcon';
import MedicalIcon from '../icons/MedicalIcon';
import BuildingIcon from '../icons/BuildingIcon';
import MarketersIcon from '../icons/MarketersIcon';
import EyeIcon from '../icons/EyeIcon';
import TargetIcon from '../icons/TargetIcon';
import GearIcon from '../icons/GearIcon';
import CommunityIcon from '../icons/CommunityIcon';
import CheckmarkIcon from '../icons/CheckmarkIcon';
import LocationIcon from '../icons/LocationIcon';
import PhoneIcon from '../icons/PhoneIcon';
import ClockIcon from '../icons/ClockIcon';
import EmailIcon from '../icons/EmailIcon';
import ArrowSendIcon from '../icons/ArrowSendIcon';

// Import MUI icons for social media
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';

import logger from '../../utils/logger';
// Dashboard Layout Styled Components
const DashboardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#F8F8F8',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column'
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: '#0B91D6',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '80px',
  padding: '0 30px'
}));

const HeaderLogo = styled('img')(({ theme }) => ({
  height: '40px',
  width: 'auto'
}));

const HeaderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px'
}));

const NotificationButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  }
}));

const NotificationMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: '8px',
    minWidth: '320px',
    maxWidth: '400px',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: '1px solid #E5E7EB'
  }
}));

const NotificationItem = styled(MenuItem)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: '1px solid #F3F4F6',
  '&:last-child': {
    borderBottom: 'none'
  },
  '&:hover': {
    backgroundColor: '#F8F9FA'
  }
}));

const NotificationHeader = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderBottom: '1px solid #E5E7EB',
  backgroundColor: '#F8F9FA'
}));

const NotificationTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '16px',
  color: '#1F2937'
}));

const NotificationText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#4B5563',
  marginBottom: '4px'
}));

const NotificationTime = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: '#9CA3AF'
}));

const UnreadDot = styled(Box)(({ theme }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#EF4444',
  marginRight: '8px',
  flexShrink: 0
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundColor: '#F8F8F8'
}));

// Page Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f8f8',
  padding: '80px 0',
  minHeight: '600px',
  display: 'flex',
  alignItems: 'center'
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: '80px 0'
}));

const PillarCard = styled(Paper)(({ theme }) => ({
  padding: '40px 24px',
  textAlign: 'center',
  borderRadius: '13px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '20px',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.1)'
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '16px'
}));

const TargetAudienceCard = styled(Paper)(({ theme }) => ({
  padding: '32px 24px',
  textAlign: 'center',
  borderRadius: '13px',
  border: '0.8px solid #f6f0f0',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '20px',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0px 15px 30px rgba(0, 0, 0, 0.08)'
  }
}));

const ValueCard = styled(Paper)(({ theme }) => ({
  padding: '32px 24px',
  textAlign: 'center',
  borderRadius: '11px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '20px',
  boxShadow: '0px 10px 21px rgba(0, 0, 0, 0.10), 0px 154px 62px rgba(0, 0, 0, 0.01), 0px 241px 67px rgba(0, 0, 0, 0.00)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.15)'
  }
}));

const WhySection = styled(Box)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  color: '#ffffff',
  padding: '80px 0',
  position: 'relative'
}));


const AboutUsPage = () => {
  const theme = useTheme();
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const notificationOpen = Boolean(notificationAnchor);

  // Mock notifications data - replace with real data from your notification service
  const notifications = [
    {
      id: 1,
      title: 'New Review Received',
      message: 'You have received a 5-star review from John Doe',
      time: '2 minutes ago',
      unread: true
    },
    {
      id: 2,
      title: 'Profile Update Required',
      message: 'Your business hours need to be updated',
      time: '1 hour ago',
      unread: true
    },
    {
      id: 3,
      title: 'Performance Report Ready',
      message: 'Your monthly performance report is now available',
      time: '3 hours ago',
      unread: false
    },
    {
      id: 4,
      title: 'New Photo Added',
      message: 'A customer has added a photo to your business',
      time: '1 day ago',
      unread: false
    },
    {
      id: 5,
      title: 'Competitor Analysis Complete',
      message: 'Your competitor analysis report is ready to view',
      time: '2 days ago',
      unread: false
    }
  ];

  const unreadCount = notifications.filter(n => n.unread).length;

  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleNotificationItemClick = (notificationId) => {
    // Handle notification click - navigate to relevant page or mark as read
    logger.log('Notification clicked:', notificationId);
    handleNotificationClose();
  };

  const pillars = [
    {
      icon: <SearchIcon width={29} height={29} color="#ffffff" />,
      title: 'Diagnose',
      description: 'We start by analyzing your Google Business Profile and local presence. From missing details to performance gaps, Visibeen gives you a clear picture of where you stand.',
      bgColor: '#4CAF50'
    },
    {
      icon: <BarChartIcon width={23} height={21} color="#ffffff" />,
      title: 'Benchmarking',
      description: 'With the Visibeen™ Score️, you can instantly compare your visibility against competitors and industry standards. It\'s your compass for local SEO success.',
      bgColor: '#2196F3'
    },
    {
      icon: <TaskIcon width={28} height={28} color="#ffffff" />,
      title: 'Tasks',
      description: 'Insights turn into actions. Visibeen generates prioritized, AI-powered tasks that help you improve step by step — from posting updates to managing reviews.',
      bgColor: '#4CAF50'
    },
    {
      icon: <ResultsIcon width={26} height={24} color="#ffffff" />,
      title: 'Results',
      description: 'Track measurable growth in visibility, traffic, and leads. Every completed task feeds back into your score, ensuring progress is visible and rewarding.',
      bgColor: '#2196F3'
    }
  ];

  const features = [
    {
      icon: <TrophyIcon width={20} height={20} color="#0b91d6" />,
      title: 'Visibeen™ Score️',
      description: 'Your live benchmark of local visibility.'
    },
    {
      icon: <LightbulbIcon width={13} height={18} color="#0b91d6" />,
      title: 'Smart Insights',
      description: 'AI-powered recommendations tailored to your profile.'
    },
    {
      icon: <PeopleIcon width={18} height={13} color="#0b91d6" />,
      title: 'Competitor Edge',
      description: 'Know exactly how you stack up and how to outrank'
    },
    {
      icon: <GrowthToolsIcon width={20} height={20} color="#0b91d6" />,
      title: 'Growth Tools',
      description: 'From reviews to posts, everything you need to shine locally'
    }
  ];

  const targetAudiences = [
    {
      icon: <ShopIcon width={41} height={37} color="#2196F3" />,
      title: 'Local Shops',
      description: 'A local shop looking to attract more walk-ins and increase foot traffic through better online visibility.'
    },
    {
      icon: <MedicalIcon width={42} height={42} color="#2196F3" />,
      title: 'Clinics & Services',
      description: 'A clinic or service brand building trust and reach in their local community.'
    },
    {
      icon: <BuildingIcon width={50} height={50} color="#2196F3" />,
      title: 'Multi-location Businesses',
      description: 'A multi-location business managing 25+ profiles across different areas.'
    },
    {
      icon: <MarketersIcon width={44} height={34} color="#2196F3" />,
      title: 'Marketers & Agencies',
      description: 'A marketer or agency aiming to deliver measurable results for their clients.'
    }
  ];

  const values = [
    {
      icon: <EyeIcon width={22} height={15} color="#ffffff" />,
      title: 'Transparency',
      description: 'No jargon, no hidden work. You always know where you stand.',
      bgColor: '#2196F3'
    },
    {
      icon: <LightbulbIcon width={13} height={18} color="#ffffff" />,
      title: 'Simplicity',
      description: 'SEO shouldn\'t be complicated. We make it practical and easy to act on.',
      bgColor: '#FFC107'
    },
    {
      icon: <TargetIcon width={24} height={24} color="#ffffff" />,
      title: 'Impact First',
      description: 'Every feature is designed to create real business results.',
      bgColor: '#2196F3'
    },
    {
      icon: <GearIcon width={18} height={20} color="#ffffff" />,
      title: 'Innovation',
      description: 'We stay ahead of trends so your business does too.',
      bgColor: '#f44336'
    },
    {
      icon: <CommunityIcon width={21} height={20} color="#ffffff" />,
      title: 'Community',
      description: 'We grow when our clients grow. Your success is our success.',
      bgColor: '#3f51b5'
    }
  ];

  const benefits = [
    'Improve rankings faster',
    'Turn visibility into customers',
    'Measurable business impact',
    'Expert guidance every step'
  ];

  return (
    <DashboardContainer>
      <HeaderSection>
        <HeaderLogo src={logo} alt="Visibeen" />
        <HeaderActions>
          <NotificationButton
            onClick={handleNotificationClick}
            aria-label={`${unreadCount} notifications`}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </NotificationButton>
          <UserAccountDropdown />
        </HeaderActions>
      </HeaderSection>

      <NotificationMenu
        anchorEl={notificationAnchor}
        open={notificationOpen}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <NotificationHeader>
          <NotificationTitle>Notifications</NotificationTitle>
        </NotificationHeader>
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              onClick={() => handleNotificationItemClick(notification.id)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                {notification.unread && <UnreadDot />}
                <Box sx={{ flex: 1 }}>
                  <NotificationText sx={{ fontWeight: notification.unread ? 600 : 400 }}>
                    {notification.title}
                  </NotificationText>
                  <NotificationText>
                    {notification.message}
                  </NotificationText>
                  <NotificationTime>
                    {notification.time}
                  </NotificationTime>
                </Box>
              </Box>
            </NotificationItem>
          ))
        ) : (
          <NotificationItem>
            <NotificationText>No notifications</NotificationText>
          </NotificationItem>
        )}
        <Divider />
        <NotificationItem sx={{ justifyContent: 'center', color: '#0B91D6', fontWeight: 600 }}>
          View All Notifications
        </NotificationItem>
      </NotificationMenu>

      <ContentArea>
        <Box>
          {/* Hero Section */}
          <HeroSection>
            <Container maxWidth="lg">
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={8} alignItems="center">
                <Stack spacing={3} flex={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#0b91d6',
                      fontWeight: 500,
                      fontSize: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}
                  >
                    About Us
                  </Typography>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '32px', md: '44px' },
                      fontWeight: 600,
                      lineHeight: { xs: '40px', md: '60px' },
                      color: '#121927'
                    }}
                  >
                    Be Seen. Be Chosen.<br />
                    <Box component="span" sx={{ color: '#0b91d6' }}>Be Local.</Box>
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '16px',
                      lineHeight: '26px',
                      color: '#000000',
                      maxWidth: '500px'
                    }}
                  >
                    At Visibeen, we believe visibility is the new currency. Every local business deserves to be discovered where it matters most — on Google. That's why we built Visibeen: a powerful yet simple Local SEO platform that helps you show up, stand out, and succeed.
                  </Typography>
                </Stack>
                <Box flex={1} sx={{ textAlign: 'center' }}>
                  <img
                    src="/images/hero-computer-map.jpg"
                    alt="Computer screen showing map with location pins for local SEO"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '16px'
                    }}
                  />
                </Box>
              </Stack>
            </Container>
          </HeroSection>

          {/* Pillars Section */}
          <SectionContainer>
            <Container maxWidth="lg">
              <Stack spacing={6} alignItems="center">
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: '32px', md: '40px' },
                      fontWeight: 500,
                      color: '#121927'
                    }}
                  >
                    The Pillars of Visibeen
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '16px',
                      color: '#121927',
                      maxWidth: '800px'
                    }}
                  >
                    Our platform is built on four core pillars that guide every feature and every client journey:
                  </Typography>
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={3}
                  sx={{ width: '100%' }}
                >
                  {pillars.map((pillar, index) => (
                    <PillarCard key={index} elevation={2}>
                      <IconWrapper sx={{ backgroundColor: pillar.bgColor }}>
                        {pillar.icon}
                      </IconWrapper>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: '20.51px',
                          fontWeight: 600,
                          color: '#121927'
                        }}
                      >
                        {pillar.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '12px',
                          lineHeight: '18px',
                          color: '#121927',
                          textAlign: 'center'
                        }}
                      >
                        {pillar.description}
                      </Typography>
                    </PillarCard>
                  ))}
                </Stack>
              </Stack>
            </Container>
          </SectionContainer>

          {/* Who We Are Section */}
          <SectionContainer sx={{ backgroundColor: '#ffffff' }}>
            <Container maxWidth="lg">
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={8} alignItems="center">
                <Box flex={1}>
                  <img
                    src="/images/analytics-person.jpg"
                    alt="Person working with analytics dashboard"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '16px'
                    }}
                  />
                </Box>
                <Stack spacing={3} flex={1}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: '32px', md: '40px' },
                      fontWeight: 500,
                      color: '#121927'
                    }}
                  >
                    Who We Are
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '14px',
                      lineHeight: '22.4px',
                      color: '#000000'
                    }}
                  >
                    We're not just a software company — we're a team of marketers, builders, and problem-solvers passionate about helping businesses thrive in the digital-first world. From family-run shops to fast-growing brands, our mission is to make local search easy, transparent, and result-driven. We understand the challenges local businesses face in competing with larger players, and we exist to level that playing field.
                  </Typography>
                </Stack>
              </Stack>
            </Container>
          </SectionContainer>

          {/* What We Do Section */}
          <SectionContainer sx={{ backgroundColor: '#f8f8f8' }}>
            <Container maxWidth="lg">
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={8} alignItems="center">
                <Stack spacing={4} flex={1}>
                  <Stack spacing={2}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#0b91d6',
                        fontWeight: 500,
                        fontSize: '12px',
                        textTransform: 'uppercase'
                      }}
                    >
                      What We Do
                    </Typography>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { xs: '32px', md: '44px' },
                        fontWeight: 500,
                        lineHeight: '53.44px',
                        color: '#121927'
                      }}
                    >
                      Visibeen transforms complex SEO into clear, actionable steps
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '12px',
                        lineHeight: '18px',
                        color: '#000000'
                      }}
                    >
                      With Visibeen, you don't just get data — you get direction. We handle the heavy lifting so you can focus on running your business while still growing visibility, trust, and revenue.
                    </Typography>
                  </Stack>

                  <Stack spacing={2}>
                    {features.map((feature, index) => (
                      <Stack key={index} direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{
                          backgroundColor: '#0b91d6',
                          borderRadius: '4px',
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '32px',
                          height: '32px'
                        }}>
                          {feature.icon}
                        </Box>
                        <Stack spacing={0.5}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: '14px',
                              fontWeight: 500,
                              color: '#000000'
                            }}
                          >
                            {feature.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '14px',
                              color: '#000000'
                            }}
                          >
                            {feature.description}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>

                <Box flex={1} sx={{ textAlign: 'center' }}>
                  <img
                    src="/images/seo-illustration.png"
                    alt="SEO optimization illustration with rankings"
                    style={{
                      maxWidth: '100%',
                      height: 'auto'
                    }}
                  />
                </Box>
              </Stack>
            </Container>
          </SectionContainer>

          {/* Who We Help Section */}
          <SectionContainer>
            <Container maxWidth="lg">
              <Stack spacing={6} alignItems="center">
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: '32px', md: '40px' },
                      fontWeight: 500,
                      color: '#121927'
                    }}
                  >
                    Who We Help
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '16px',
                      color: '#121927'
                    }}
                  >
                    Visibeen is designed for businesses of all sizes and industries:
                  </Typography>
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={3}
                  sx={{ width: '100%' }}
                >
                  {targetAudiences.map((audience, index) => (
                    <TargetAudienceCard key={index} elevation={1}>
                      <Box sx={{ color: audience.icon.props.color }}>
                        {audience.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: '20.51px',
                          fontWeight: 600,
                          color: '#121927'
                        }}
                      >
                        {audience.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '11.04px',
                          lineHeight: '18px',
                          color: '#121927',
                          textAlign: 'center'
                        }}
                      >
                        {audience.description}
                      </Typography>
                    </TargetAudienceCard>
                  ))}
                </Stack>

                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '16px',
                    color: '#121927',
                    textAlign: 'center',
                    maxWidth: '800px'
                  }}
                >
                  If being found on Google matters to your success, Visibeen is built for you.
                </Typography>
              </Stack>
            </Container>
          </SectionContainer>

          {/* Our Values Section */}
          <SectionContainer sx={{ backgroundColor: '#f8f8f8' }}>
            <Container maxWidth="lg">
              <Stack spacing={6} alignItems="center">
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: '32px', md: '40px' },
                      fontWeight: 500,
                      color: '#121927'
                    }}
                  >
                    Our Values
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '16px',
                      color: '#121927'
                    }}
                  >
                    Our work is guided by principles that keep us focused on what matters
                  </Typography>
                </Stack>

                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)'
                  },
                  gap: 3,
                  width: '100%'
                }}>
                  {values.map((value, index) => (
                    <ValueCard key={index} elevation={3}>
                      <IconWrapper sx={{ backgroundColor: value.bgColor }}>
                        {value.icon}
                      </IconWrapper>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#121927'
                        }}
                      >
                        {value.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '12px',
                          lineHeight: '18px',
                          color: '#121927',
                          textAlign: 'center'
                        }}
                      >
                        {value.description}
                      </Typography>
                    </ValueCard>
                  ))}
                </Box>
              </Stack>
            </Container>
          </SectionContainer>

          {/* Why Visibeen Section */}
          <WhySection>
            <Container maxWidth="lg">
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={8} alignItems="center">
                <Box flex={1}>
                  <img
                    src="/images/robot-ai.png"
                    alt="AI robot working with analytics"
                    style={{
                      maxWidth: '100%',
                      height: 'auto'
                    }}
                  />
                </Box>
                <Stack spacing={4} flex={1}>
                  <Stack spacing={2}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { xs: '32px', md: '44px' },
                        fontWeight: 500,
                        color: '#ffffff'
                      }}
                    >
                      Why Visibeen?
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '12px',
                        lineHeight: '18px',
                        color: '#ffffff'
                      }}
                    >
                      Because being visible online isn't enough. You need to be trusted, chosen, and remembered. Visibeen gives you the tools, insights, and ongoing guidance to make that happen — faster, smarter, and with measurable impact. We don't just improve rankings; we help you turn visibility into customers, revenue, and growth. That's the Visibeen promise.
                    </Typography>
                  </Stack>

                  <Stack spacing={2}>
                    {benefits.map((benefit, index) => (
                      <Stack key={index} direction="row" spacing={2} alignItems="center">
                        <CheckmarkIcon width={18} height={18} color="#4CAF50" />
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#ffffff'
                          }}
                        >
                          {benefit}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Stack>
            </Container>
          </WhySection>

          {/* Contact Section */}
          <SectionContainer>
            <Container maxWidth="lg">
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={8}>
                <Stack spacing={3} flex={1}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#0b91d6',
                      fontWeight: 500,
                      fontSize: '12px',
                      textTransform: 'uppercase'
                    }}
                  >
                    Contact Us
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: '32px', md: '44px' },
                      fontWeight: 500,
                      lineHeight: '50.91px',
                      color: '#121927'
                    }}
                  >
                    Have Questions?<br />Get in touch!
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '14.42px',
                      lineHeight: '23.37px',
                      color: '#121927'
                    }}
                  >
                    Have questions or need support? We're here to help—reach out to our team anytime.
                  </Typography>
                </Stack>

                <Stack spacing={3} flex={1}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{
                      backgroundColor: '#0b91d6',
                      borderRadius: '4px',
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <LocationIcon width={13} height={19} color="#ffffff" />
                    </Box>
                    <Stack>
                      <Typography variant="body2" sx={{ color: '#121927' }}>
                        Office No A-611, 6th floor, Bestech Tower
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#121927' }}>
                        Mohali, SAS Nagar, Punjab
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      backgroundColor: '#0b91d6',
                      borderRadius: '4px',
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PhoneIcon width={11} height={19} color="#ffffff" />
                    </Box>
                    <Typography variant="body1" sx={{ color: '#121927' }}>
                      +91-9815009722
                      <br />
                      +91-9115959722
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{
                      backgroundColor: '#0b91d6',
                      borderRadius: '4px',
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ClockIcon width={19} height={19} color="#ffffff" />
                    </Box>
                    <Stack>
                      <Typography variant="body2" sx={{ color: '#121927' }}>
                        Mon-Fri: 9 AM – 6 PM
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#121927' }}>
                        Sunday: Closed
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      backgroundColor: '#0b91d6',
                      borderRadius: '4px',
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <EmailIcon width={17} height={13} color="#ffffff" />
                    </Box>
                    <Typography variant="body1" sx={{ color: '#121927' }}>
                      support@visibeen.com
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Container>
          </SectionContainer>
        </Box>
      </ContentArea>
      <Footer />
    </DashboardContainer>
  );
};

export default AboutUsPage;